import {Component, Input} from '@angular/core';
import {
  CommonError,
  ErrorType,
  InvalidValueError,
  RangeError,
  UnexpectedValueError,
  ValidationError,
  ValidatorResult,
  WrongListSizeError,
} from "@model";
import {TranslocoService} from "@ngneat/transloco";
import dayjs from "dayjs";
import {localizedDateString} from "@utility";

@Component({
    selector: 'validator-result-message',
    templateUrl: './validator-result-message.component.html',
    styleUrls: ['./validator-result-message.component.scss'],
    standalone: false
})
export class ValidatorResultMessage {
  @Input() validatorResult: ValidatorResult | undefined;
  @Input() indent: number = 0;
  @Input() key: string = "";
  @Input() keyPrefix: string | undefined;
  @Input() fieldId: number | undefined;
  @Input() fieldValue?: string = "";

  errorMessages: UserMessage[] = [];
  rowName: string | undefined;

  constructor(private translocoService: TranslocoService) {
  }

  ngOnInit() {
    if (this.validatorResult?.errors) {
      const required: string[] = [];
      this.validatorResult.errors.forEach((error: ValidationError) => {
        if (error.errorType === ErrorType.REQUIRED) {
          required.push(this.translocoService.translate((this.keyPrefix ?? "app.common.") + error.key));
        }
        else if (error.errorType === ErrorType.OTHER) {
          const commonError = error as CommonError;
          this.errorMessages.push(new UserMessage("", this.translocoService.translate(commonError.translationMessageKey)))
        }
        else {
          let userMessage = new UserMessage(this.translocoService.translate((this.keyPrefix ?? "app.common.") + error.key), "");
          switch (error.errorType) {
            case ErrorType.INVALID_VALUE:
              const invalidValueError = error as InvalidValueError;
              userMessage.message = this.translocoService.translate("app.validation.error.invalidValue", {
                invalidValue: this.getInvalidValueTranslation(invalidValueError, [invalidValueError.invalidValue]),
                validValues: this.getInvalidValueTranslation(invalidValueError, invalidValueError.validValues)
              })
              break;
            case ErrorType.UNEXPECTED_VALUE:
              const unexpectedValueError = error as UnexpectedValueError;
              userMessage.message = this.translocoService.translate("app.validation.error.unexpectedValue", {
                value: unexpectedValueError.unexpectedValue,
              })
              break;
            case ErrorType.WRONG_LIST_SIZE:
              const wrongListSizeError: WrongListSizeError = error as WrongListSizeError;
              userMessage.message =
                this.translocoService.translate(
                  (wrongListSizeError.actual === 0 && wrongListSizeError.expected === 1) ?
                    "app.validation.error.wrongListSize.required" : "app.validation.error.wrongListSize", {
                    actual: wrongListSizeError.actual,
                    expected: wrongListSizeError.expected
                  })
              break;
            case ErrorType.RANGE:
              const rangeError: RangeError = error as RangeError;
              userMessage.message = this.translocoService.translate("app.validation.error.range", {
                min: this.getInvalidRangeValue(rangeError.min),
                max: this.getInvalidRangeValue(rangeError.max)
              })
              break;
            default:
              break;
          }
          this.errorMessages.push(userMessage);
        }
      })
      if (required.length > 0) {
        this.errorMessages.push(new UserMessage(required.filter(((value, index, self) => self.indexOf(value) === index)).join(", "), " - " + this.translocoService.translate("app.validation.error.required.fill")));
      }
    }
    if (this.key.length > 0) {
        this.rowName = `${this.translocoService.translate((this.keyPrefix ?? "app.common.") + this.key).toUpperCase()}`.toUpperCase();
    }
    if ((this.fieldValue?.length ?? 0) > 0) {
      this.rowName = `${this.rowName}${" - " + this.fieldValue}`.toUpperCase();
    }
    else if (this.fieldId) {
      this.rowName = `${this.rowName}${" (" + this.translocoService.translate("app.common.id") + " = " + this.fieldId + ")"}`.toUpperCase();

    }
  }


  getInvalidRangeValue(value: any): string {
    const dateValue = dayjs(value, "Z");
    if (dateValue.isValid()) {
      return localizedDateString(value, "short", this.translocoService.getActiveLang());
    } else {
      return value;
    }
  }

  getInvalidValueTranslation(error: InvalidValueError, transformValues: string[]): string {
    let returnValue = "";
    transformValues.forEach((value, index) => {
      if (error.codebookMapping) {

        if (error.key.toLowerCase().includes("RegistrationDisplayFlag".toLowerCase())) {
          returnValue += this.translocoService.translate("app.common.registrationDisplayFlag." + value)
        } else {
          returnValue += this.translocoService.translate((this.keyPrefix ?? "app.common.") + error.key + "." + error.invalidValue);
        }

      } else if (typeof value === 'boolean') {
        returnValue += this.translocoService.translate("app.common." + (value ? "yes" : "no")).toUpperCase()
      }
      if (index < transformValues.length - 1) {
        returnValue += ", "
      }
    })

    return returnValue;
  }
}


class UserMessage {
  constructor(public field: string, public message: string | undefined) {
  }
}


